import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Star, ArrowLeft, Languages, Clock3, Sparkles, Sun, Sunrise, Sunset, Award, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatHourLabel, friendlyDayPart, dateAtHour, isSameDay, isHourPast } from "@/lib/time";
import InitialsAvatar from "@/components/InitialsAvatar";

const FALLBACK_HOURS = [];

export default function TherapistDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [date, setDate] = useState();
  const [hour, setHour] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [availability, setAvailability] = useState({
    all_hours: FALLBACK_HOURS,
    available_hours: FALLBACK_HOURS,
    booked_hours: [],
    blocked_hours: [],
    working_day: true,
  });
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState();
  const [customTime, setCustomTime] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    api.get(`/therapists/${id}`).then(({ data }) => setT(data)).catch(() => setT(false));
    if (user) {
      api.get("/bookings").then(({ data }) => setMyBookings(data)).catch(() => {});
    } else {
      setMyBookings([]);
    }
  }, [id, user]);

  useEffect(() => {
    if (!date) return;
    const yyyy = format(date, "yyyy-MM-dd");
    setHour(null);
    api
      .get(`/therapists/${id}/availability`, { params: { date: yyyy } })
      .then(({ data }) => setAvailability(data))
      .catch(() =>
        setAvailability({ all_hours: FALLBACK_HOURS, available_hours: FALLBACK_HOURS, booked_hours: [], blocked_hours: [], working_day: true })
      );
  }, [date, id]);

  const upcoming = useMemo(
    () => myBookings.filter((b) => b.therapist_id === id && b.status !== "cancelled"),
    [myBookings, id]
  );

  const today = new Date();
  const dateIsToday = date && isSameDay(date, today);

  const book = async () => {
    if (!user) {
      toast.message("Sign in to book a session.");
      navigate("/login");
      return;
    }
    if (!date || !hour) {
      toast.error("Pick a day and a time first.");
      return;
    }
    setBusy(true);
    try {
      const slot = dateAtHour(date, hour);
      const { data } = await api.post("/bookings", {
        therapist_id: id,
        slot: slot.toISOString(),
        note,
      });
      toast.success(`Booked with ${data.therapist_name} on ${format(new Date(data.slot), "PPpp")}.`);
      setHour(null);
      setNote("");
      const res = await api.get("/bookings");
      setMyBookings(res.data);
      const yyyy = format(date, "yyyy-MM-dd");
      const av = await api.get(`/therapists/${id}/availability`, { params: { date: yyyy } });
      setAvailability(av.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not book.");
    } finally {
      setBusy(false);
    }
  };

  const sendCustomRequest = async () => {
    if (!user) {
      toast.message("Sign in to send a request.");
      navigate("/login");
      return;
    }
    if (!customDate || !customTime) {
      toast.error("Pick a date and a time.");
      return;
    }
    setRequesting(true);
    try {
      const slot = dateAtHour(customDate, customTime);
      if (slot.getTime() <= Date.now()) {
        toast.error("Pick a future time.");
        return;
      }
      await api.post("/slot-requests", {
        therapist_id: id,
        requested_slot: slot.toISOString(),
        note: customNote,
      });
      toast.success("Sent! The counselor will reply by email.");
      setShowCustom(false);
      setCustomDate(undefined);
      setCustomTime("");
      setCustomNote("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send request.");
    } finally {
      setRequesting(false);
    }
  };

  if (t === null) return <div className="max-w-3xl mx-auto px-6 py-20 text-mindora-muted">Loading…</div>;
  if (t === false) return <div className="max-w-3xl mx-auto px-6 py-20">Counselor not found.</div>;

  const settings = availability.settings || {};
  // Show ONLY the times the counsellor has actually opened — no fallback grid
  const slotList = availability.all_hours || [];
  // Group slots by morning/afternoon/evening for friendlier scanning
  const grouped = { morning: [], afternoon: [], evening: [] };
  slotList.forEach((h) => grouped[friendlyDayPart(h) || "afternoon"].push(h));

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="page-therapist-detail">
      <div className="lg:col-span-5">
        <Link to="/directory" className="inline-flex items-center gap-2 text-sm text-mindora-muted hover:text-mindora-red mb-8" data-testid="therapist-back">
          <ArrowLeft size={14} /> back
        </Link>
        <div className="rounded-3xl overflow-hidden border border-mindora-line shadow-clay aspect-square">
          {t.photo_url ? (
            <img src={t.photo_url} alt={t.name} className="w-full h-full object-cover" />
          ) : (
            <InitialsAvatar name={t.name} className="w-full h-full" testId="therapist-avatar" />
          )}
        </div>
        <h1 className="font-serif text-4xl text-mindora-ink mt-7 leading-tight">{t.name}</h1>
        <div className="text-xs uppercase tracking-[0.2em] text-mindora-red mt-1">{t.title}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 text-sm text-mindora-muted">
          <span className="inline-flex items-center gap-1.5"><Star size={14} className="text-mindora-red" fill="#A73C35" /> {t.rating}</span>
          {t.experience && (
            <span className="inline-flex items-center gap-1.5" data-testid="therapist-experience">
              <Award size={14} /> {t.experience}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5"><Languages size={14} /> {t.languages.join(", ")}</span>
        </div>
        <p className="mt-6 text-mindora-ink/80 leading-relaxed">{t.bio}</p>
        <div className="mt-5 flex flex-wrap gap-1.5">
          {t.specialties.map((s) => (
            <span key={s} className="text-[10px] uppercase tracking-[0.16em] text-mindora-ink/70 border border-mindora-line rounded-full px-2.5 py-0.5">
              {s}
            </span>
          ))}
        </div>
      </div>

      <div className="lg:col-span-7">
        <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-3">
          <span className="editorial-rule" />Book a session
        </div>
        <h2 className="font-serif text-4xl text-mindora-ink leading-tight" data-testid="therapist-price">
          {t.session_price_display || `₹${t.session_price}`}
          <span className="text-lg text-mindora-muted">
            {settings.session_duration_min ? ` / ${settings.session_duration_min}-min session` : " / 50-min session"}
          </span>
        </h2>

        {user?.role === "user" && (
          <Button
            onClick={async () => {
              try {
                const { data } = await api.post("/conversations", { therapist_id: t.id });
                navigate(`/messages?c=${data.id}`);
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not open chat");
              }
            }}
            variant="outline"
            data-testid="therapist-message-button"
            className="mt-5 w-full rounded-full border-mindora-line text-mindora-ink hover:border-mindora-red hover:text-mindora-red h-11"
          >
            <MessageSquare size={14} className="mr-2" />
            Message {t.name.split(" ")[0]}
          </Button>
        )}

        {!user && (
          <div className="mt-5 rounded-2xl border border-mindora-red/30 bg-mindora-red/5 p-4 text-sm text-mindora-ink/90">
            Browse times freely.{" "}
            <Link to="/login" className="text-mindora-red underline hover:no-underline">Sign in</Link>{" "}
            (or{" "}
            <Link to="/register" className="text-mindora-red underline hover:no-underline">create a space</Link>
            ) to confirm a booking.
          </div>
        )}

        {/* STEP 1 — pick a day */}
        <section className="mt-8" data-testid="booking-step-day">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80">Step 1 · Pick a day</div>
              <p className="text-xs text-mindora-muted mt-1">
                {date ? format(date, "EEEE, MMMM do") : "Tap a date on the calendar to see available times."}
              </p>
            </div>
            {date && (
              <button onClick={() => setDate(undefined)} className="text-xs text-mindora-muted hover:text-mindora-red">change</button>
            )}
          </div>
          <div className="mt-3 rounded-2xl border border-mindora-line bg-mindora-panel p-3 inline-block">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              data-testid="therapist-calendar"
            />
          </div>
        </section>

        {/* STEP 2 — pick a time */}
        {date && (
          <section className="mt-10" data-testid="booking-step-time">
            <div className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80">Step 2 · Pick a time <span className="text-mindora-muted normal-case tracking-normal text-[10px] ml-1">(your local time)</span></div>

            {!availability.working_day ? (
              <div className="mt-3 rounded-2xl border border-mindora-line bg-mindora-panel p-5 text-sm text-mindora-ink/80" data-testid="empty-day-banner">
                {t.name.split(" ")[0]} hasn&apos;t opened any times for {format(date, "EEEE")}s yet.{" "}
                <button onClick={() => setShowCustom(true)} className="text-mindora-red underline hover:no-underline" data-testid="empty-day-request">
                  Request a custom time
                </button>{" "}
                and {t.name.split(" ")[0]} will get back to you.
              </div>
            ) : slotList.length === 0 ? (
              <div className="mt-3 rounded-2xl border border-mindora-line bg-mindora-panel p-5 text-sm text-mindora-ink/80" data-testid="no-slots-banner">
                {t.name.split(" ")[0]} hasn&apos;t added any open times here yet.{" "}
                <button onClick={() => setShowCustom(true)} className="text-mindora-red underline hover:no-underline" data-testid="no-slots-request">
                  Request a custom time
                </button>{" "}
                — they&apos;ll review and confirm.
              </div>
            ) : (
              <div className="mt-4 space-y-5" data-testid="time-groups">
                {(["morning", "afternoon", "evening"]).map((part) => {
                  const hrs = grouped[part] || [];
                  if (hrs.length === 0) return null;
                  const Icon = part === "morning" ? Sunrise : part === "afternoon" ? Sun : Sunset;
                  return (
                    <div key={part} data-testid={`time-group-${part}`}>
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-mindora-red mb-2.5">
                        <Icon size={12} /> {part}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {hrs.map((h) => {
                          const isBooked = (availability.booked_hours || []).includes(h);
                          const isBlocked = (availability.blocked_hours || []).includes(h);
                          const isPast = dateIsToday && isHourPast(date, h);
                          const isUnavailable = isBooked || isBlocked || isPast;
                          const label = formatHourLabel(h);
                          let aside = "";
                          if (isPast) aside = "past";
                          else if (isBooked) aside = "booked";
                          else if (isBlocked) aside = "unavail.";
                          return (
                            <button
                              key={h}
                              type="button"
                              onClick={() => !isUnavailable && setHour(h)}
                              disabled={isUnavailable}
                              data-testid={`therapist-slot-${h}`}
                              title={isPast ? "Earlier than now" : isBooked ? "Already booked" : isBlocked ? "Counselor blocked this slot" : ""}
                              className={`relative rounded-xl border py-3 text-sm transition-all flex flex-col items-center justify-center ${
                                hour === h
                                  ? "bg-mindora-red text-mindora-bg border-mindora-red shadow-clay"
                                  : isUnavailable
                                  ? "bg-mindora-panel/60 border-mindora-line text-mindora-muted line-through cursor-not-allowed"
                                  : "bg-mindora-bg border-mindora-line text-mindora-ink hover:border-mindora-red hover:shadow-clay"
                              }`}
                            >
                              <span className="font-medium">{label}</span>
                              {aside && (
                                <span className="block text-[9px] uppercase tracking-wider mt-0.5">{aside}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {(availability.available_hours || []).filter((h) => !dateIsToday || !isHourPast(date, h)).length === 0 && (
                  <div className="rounded-2xl border border-mindora-line bg-mindora-bg p-5 text-sm text-mindora-ink/80">
                    No times open on this day.{" "}
                    <button onClick={() => setShowCustom(true)} className="text-mindora-red underline hover:no-underline" data-testid="no-times-request">
                      Request a custom time
                    </button>{" "}
                    or pick another day.
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* STEP 3 — confirm */}
        {date && hour && (
          <section className="mt-10" data-testid="booking-step-confirm">
            <div className="text-xs uppercase tracking-[0.2em] text-mindora-ink/80 mb-3">Step 3 · Confirm</div>
            <div className="rounded-2xl border border-mindora-line bg-mindora-panel p-5">
              <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.2em]">
                <Clock3 size={12} /> You picked
              </div>
              <div className="font-serif text-2xl text-mindora-ink mt-2">
                {format(date, "EEEE, MMMM do")} · {formatHourLabel(hour)}
              </div>
              <div className="text-xs text-mindora-muted mt-1">{friendlyDayPart(hour)} — your local time</div>

              <Textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="therapist-note-input"
                placeholder="Anything they should know before the session? (optional)"
                className="mt-4 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
              />

              <Button
                onClick={book}
                disabled={busy}
                data-testid="therapist-book-button"
                className="mt-4 w-full btn-primary rounded-full h-11"
              >
                {busy ? "Booking…" : !user ? "Sign in to confirm" : `Confirm — ${formatHourLabel(hour)} on ${format(date, "MMMM d")}`}
              </Button>
            </div>
          </section>
        )}

        {/* Custom request CTA */}
        <section className="mt-10 rounded-3xl border border-dashed border-mindora-red/40 bg-mindora-red/5 p-6" data-testid="custom-request-section">
          {!showCustom ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <Sparkles size={18} className="text-mindora-red shrink-0 mt-0.5" />
                <div>
                  <div className="font-serif text-xl text-mindora-ink leading-snug">Don&apos;t see a time that works?</div>
                  <p className="text-sm text-mindora-muted mt-1">Propose a custom time — your counselor will reply by email.</p>
                </div>
              </div>
              <Button
                onClick={() => setShowCustom(true)}
                data-testid="open-custom-request"
                className="rounded-full bg-mindora-ink text-mindora-bg hover:bg-mindora-red px-5 h-10 text-sm tracking-wide"
              >
                Request a custom time
              </Button>
            </div>
          ) : (
            <div data-testid="custom-request-form">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="font-serif text-xl text-mindora-ink">Request a custom time</div>
                <button onClick={() => setShowCustom(false)} className="text-xs text-mindora-muted hover:text-mindora-red">close</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
                <div className="rounded-2xl border border-mindora-line bg-mindora-bg p-3 inline-block">
                  <Calendar
                    mode="single"
                    selected={customDate}
                    onSelect={setCustomDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    data-testid="custom-calendar"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-mindora-ink/80">Time (your local time)</label>
                  <input
                    type="time"
                    value={customTime}
                    onChange={(e) => setCustomTime(e.target.value)}
                    data-testid="custom-time-input"
                    className="mt-2 w-full rounded-md border border-mindora-line bg-mindora-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mindora-red"
                  />
                  <Textarea
                    rows={4}
                    value={customNote}
                    onChange={(e) => setCustomNote(e.target.value)}
                    data-testid="custom-note-input"
                    placeholder="Why this time works for you (so your counselor can decide)…"
                    className="mt-4 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
                  />
                  <Button
                    onClick={sendCustomRequest}
                    disabled={requesting || !customDate || !customTime}
                    data-testid="custom-submit"
                    className="mt-4 w-full btn-primary rounded-full h-11"
                  >
                    {requesting ? "Sending…" : "Send request"}
                  </Button>
                  <p className="text-[11px] text-mindora-muted mt-2 italic">
                    No charge unless they accept. You&apos;ll get an email either way.
                  </p>
                </div>
              </div>
            </div>
          )}
        </section>

        {upcoming.length > 0 && (
          <div className="mt-12">
            <h3 className="font-serif text-2xl text-mindora-ink">Your sessions with {t.name.split(" ")[0]}</h3>
            <div className="mt-4 space-y-3" data-testid="therapist-my-bookings">
              {upcoming.map((b) => (
                <div key={b.id} className="rounded-xl border border-mindora-line bg-mindora-panel p-4 flex items-center justify-between">
                  <div className="font-serif text-lg text-mindora-ink">
                    {format(new Date(b.slot), "PPP")} · {format(new Date(b.slot), "h:mm a")}
                  </div>
                  <span className="text-xs uppercase tracking-[0.2em] text-mindora-red">{b.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
